import maxSize from 'popper-max-size-modifier';
import React, { FunctionComponent, MutableRefObject, ReactElement, Ref, useMemo } from 'react';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { useEffectSync } from '../../hooks/useEffectSync/useEffectSync';
import { useMergedRefs } from '../../hooks/useMergedRefs';
import { useOnOutsideClick } from '../../hooks/useOnOutsideClick';
import { usePopper } from '../../hooks/usePopper';
import { createMutableRefObject } from '../../utils/createMutableRefObject';
import { prepareProps } from '../../utils/propUtils';
export type DropdownPopperProps = {
  visible: boolean;
  renderContent: (options: {
    ref: (instance: HTMLElement | null) => void;
  }) => ReactElement<{
    ref: Ref<HTMLElement>;
  }>;
  children: (options: {
    ref: (instance: HTMLElement | null) => void;
  }) => React.JSX.Element;
  contentElementRef?: MutableRefObject<HTMLElement | null>;
  childElementRef?: MutableRefObject<HTMLElement | null>;

  /**
   * this is needed for functionality like `hideOnOutsideClick`
   */
  setVisible?: (visible: boolean) => void;
  hideOnOutsideClick?: boolean;
};
export const DropdownPopper: FunctionComponent<DropdownPopperProps> = React.memo(props => {
  const {
    visible,
    setVisible,
    hideOnOutsideClick,
    renderContent,
    childElementRef: childElementRefProp,
    contentElementRef: contentElementRefProp,
    children
  } = prepareProps({
    props
  });
  if (hideOnOutsideClick && !setVisible) {
    throw new Error("can't use hideOnOutsideClick without the setVisibleFunction");
  }
  const {
    popperInstance,
    referenceElement,
    popperElement,
    setReferenceElement,
    setPopperElement
  } = usePopper({
    placement: 'bottom',
    modifiers: [{
      name: 'offset',
      options: {
        offset: [0, 5]
      }
    }, {
      name: 'preventOverflow'
    }, maxSize, {
      name: 'applyMaxSize',
      enabled: true,
      phase: 'beforeWrite',
      requires: ['maxSize'],
      fn({
        state
      }) {
        // The `maxSize` modifier provides this data
        const {
          height
        } = state.modifiersData.maxSize;
        state.styles.popper = {
          ...state.styles.popper,
          maxHeight: `${height}px`
        };
      }
    }]
  });
  const childElementRef = useMergedRefs(setReferenceElement, childElementRefProp);
  const contentElementRef = useMergedRefs(setPopperElement, contentElementRefProp);

  // use a synchronous effect for this
  useEffectSync(() => {
    if (referenceElement && popperElement && popperInstance) {
      const clientRect = referenceElement.getBoundingClientRect();
      popperElement.style.width = `${clientRect.width}px`;
      popperElement.style.overflowY = 'auto';
      void popperInstance.update();
    }
  }, [referenceElement, popperElement, popperInstance]);
  useOnOutsideClick([createMutableRefObject(referenceElement), createMutableRefObject(popperElement)], () => {
    if (hideOnOutsideClick && visible) {
      assertNotNullOrUndefined(setVisible, "can't hideOnOutsideClick without setVisible");
      setVisible(false);
    }
  });
  const mainContent = useMemo(() => {
    return children({
      ref: childElementRef
    });
  }, [children, childElementRef]);
  const fixedContent = useMemo(() => {
    return visible ? renderContent({
      ref: contentElementRef
    }) : null;
  }, [renderContent, visible, contentElementRef]);
  return <>
        {mainContent}
        {fixedContent}
      </>;
});